/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import React, { useState } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { graphqlErrorIndicatesForbidden } from "utils/graphql-auth-error";
import { Viewer } from "components/auth/types";
import { QueryInterestProfile, QueryInterestProfileVariables } from "types/gql-types";
import { ServerError } from "components/graphql-views";
import { CloseButton } from "components/haven-design/buttons";
import BatchActionsModal from "pages/dashboard/interests-batch-actions/batch-actions-modal";
import { QUERY_INTEREST_PROFILE } from "pages/dashboard/interests-profile/query";
import styles from "./slideout.module.css";
import { ApplicantProfileView } from "./index";
import type { Action } from "pages/dashboard/interests-profile/components/reason-modal";

type Props = {
  path?: string;
  viewer: Viewer;
  onStar?: (id: string) => void;
  listId: string;
  sendFullAppToInterestsEnabled: boolean;
  onClose?: () => void;
}

const InterestProfileSlideout: React.FC<Props> = (props) => {
  const { viewer, onStar, sendFullAppToInterestsEnabled, onClose } = props;
  const { interestId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [
    batchActionsModalFocusedAction,
    setBatchActionsModalFocusedAction,
  ] = useState<Action | undefined>();

  const queryInterestProfile = useQuery<QueryInterestProfile, QueryInterestProfileVariables>(
    QUERY_INTEREST_PROFILE,
    {
      fetchPolicy: "no-cache",
      variables: { interestId: interestId || "" },
      skip: !interestId,
    },
  );

  if (!interestId) return null;
  if (queryInterestProfile.loading) return null;

  if (queryInterestProfile.error && graphqlErrorIndicatesForbidden(queryInterestProfile.error)) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (
    queryInterestProfile.error ||
    !queryInterestProfile.data?.interest?.id ||
    !queryInterestProfile.data?.interest?.interestList
  ) {
    return <ServerError desc="There was a problem loading the page." />;
  }
  const {
    interest,
    interest: {
      id,
      householdMember0FirstName: firstName,
      householdMember0LastName: lastName,
      interestList: { id: listID, property },
      householdCount,
      householdGrossAnnualIncome,
      status: { name: status },
      user,
    },
  } = queryInterestProfile.data;

  const onSubmit = () =>
    new Promise<void>((resolve) => {
      resolve();
    });

  return (
    <div className={styles.slideout}>
      <CloseButton onClick={() => {
        onClose?.();
        const { pathname } = location;
        const idx = pathname.indexOf("/interests/");
        if (idx > -1) {
          const base = pathname.slice(0, idx).replace(/\/$/, "");
          navigate(base);
        } else {
          navigate("..");
        }
      }} />
      <div style={{ height: "100%" }}>
        <ApplicantProfileView
          onStar={() => onStar && onStar(interestId)}
          location={location}
          viewer={viewer}
          setBatchActionsModalFocusedAction={setBatchActionsModalFocusedAction}
          currentInterestID={id}
          interestListID={listID}
          householdMember0FirstName={firstName}
          householdMember0LastName={lastName}
          property={property}
          householdCount={householdCount}
          householdGrossAnnualIncome={householdGrossAnnualIncome}
          status={status}
          onSubmit={onSubmit}
          sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
          interest={interest}
          user={user}
        />
        <BatchActionsModal
          focusedAction={batchActionsModalFocusedAction}
          interestListID={listID}
          viewer={viewer}
          interest={interest}
          setBatchActionsModalFocusedAction={setBatchActionsModalFocusedAction}
        />
      </div>
    </div>
  );
};

export default InterestProfileSlideout;
